import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Formik, FormikHelpers } from 'formik'
import * as Yup from 'yup'

import { Box, Button, Checkbox, RightPanel, Toast } from 'components'

// Utils and Services
import useScopeModal from 'hooks/use-scope-modal'
import useBranch from 'hooks/use-branch'
import useContactInfo from 'hooks/use-contact-info'
import useLeadDetail from 'hooks/use-lead-detail'
import useWorkFlowLookup from 'hooks/lookup/use-workflow-lookup'
import { LeadContactValidationSchema, checkLeadDuplicate } from 'modules/Leads/NewLead/validation'
import ContactSection from 'modules/shared/ContactSection'
import { useLeadContactQueryClient } from 'queries/lead-contact'
import { updateLeadContact } from 'services/lead-contact'
import { FETCHING_STATUS } from 'utils/constants'
import { getValidationMessages } from 'utils/validation'
import useAppSetting from 'hooks/use-app-setting'

// Types
import { LeadDetails } from 'types/global'

import PanelTitle from '../shared/PanelTitle'
import DeactivateContactDetails from './DeactivateContactDetails'
import DeleteContactDetails from './DeleteContactDetails'
import ReactivateContactDetails from './ReactivateContactDetails'
import { Actions, getBadge, getDeletedContactInfo, getMenuOptions } from './helper'
import { LeadDuplicateReason } from 'services/types/lead'

import { isArray, differenceBy } from 'lodash-es'

const { REQUIRED_EMAIL, REQUIRED_PHONE_NUMBER } = getValidationMessages()

type Props = {
  data?: LeadDetails.ContactPerson
  isLastActiveContact?: boolean
  onClose: (closePanel?: boolean) => void
  setNewContact?: (contact: LeadDetails.ContactPerson) => void
  zIndex?: number
}

const ContactDetails: React.FC<Props> = ({ data, isLastActiveContact = false, onClose, setNewContact, zIndex }) => {
  const { t } = useTranslation()
  const { setModal } = useScopeModal()
  const currentBranch = useBranch()

  const { key: appSettings } = useAppSetting()
  const deduplicateScopes = appSettings?.DEDUPLICATE_SCOPES || []

  const { data: leadDetail, isSuccess: isFetchLeadDetailSuccess, leadId = '' } = useLeadDetail()

  const {
    createContact,
    deleteContactInfo,
    isFetching,
    isLoading,
    refetch: fetchContacts,
    updateContact,
  } = useContactInfo({ leadId })

  const { invalidateQuery } = useLeadContactQueryClient(leadId)
  const { contactInfoTypes } = useWorkFlowLookup({})
  const [isMarkingPrimary, setMarkingPrimary] = React.useState(false)
  const isBusy = isMarkingPrimary || isFetching || isLoading

  const emailContactInfoTypeId = useMemo(
    () => contactInfoTypes.find((i) => i.name.toLowerCase() === 'email')?.id || '',
    [contactInfoTypes],
  )
  const contactInfoTypeId = contactInfoTypes.find((item) => item.isDefault)?.id || ''

  const handleConfirmationModalAction = (success?: boolean) => {
    if (!leadId) {
      return
    }

    setModal(null)

    if (success) {
      fetchContacts()
      onClose(true)
      return
    }
  }

  const handleMarkAsPrimary = async () => {
    if (!data) return

    const payload = {
      contact_id: data.id as string,
      contact_type_id: data.contactTypeId,
      email: data.email.map(({ contactInfoTypeId, id, isPrimary, value }: LeadDetails.Email) => ({
        contact_info_id: id,
        contact_info_type_id: contactInfoTypeId,
        is_primary: isPrimary,
        value,
      })),
      fname: data.fname,
      job_title: data.jobTitle,
      lead_id: leadId,
      is_primary: true,
      lname: data.lname,
      mobile: data.mobile.map(({ id, contactInfoTypeId, isPrimary, value }: LeadDetails.Mobile) => ({
        contact_info_id: id,
        contact_info_type_id: contactInfoTypeId,
        is_primary: isPrimary,
        value,
      })),
    }

    setMarkingPrimary(true)
    const response = await updateLeadContact(payload)

    setMarkingPrimary(false)
    if (response.status === FETCHING_STATUS.fail) {
      Toast.toastMessage({
        message: `lead_contact_set_primary_error`,
        variant: 'error',
      })
      return
    }

    Toast.toastMessage({
      message: `lead_contact_set_primary_success`,
      variant: 'success',
    })

    invalidateQuery()
  }

  const handleContextMenuOptionClick = (option: keyof typeof Actions) => {
    if (!leadId) {
      return
    }

    switch (option) {
      case Actions.markAsPrimary:
        handleMarkAsPrimary()
        break
      case Actions.deactivate:
        setModal(
          <DeactivateContactDetails
            contactId={initValues.contactPerson.id as string}
            isActive={initValues.contactPerson.isActive}
            onAction={handleConfirmationModalAction}
          />,
        )
        break
      case Actions.reactivate:
        setModal(
          <ReactivateContactDetails
            contactId={initValues.contactPerson.id as string}
            leadId={leadId}
            onAction={handleConfirmationModalAction}
          />,
        )
        break
      case Actions.delete:
        setModal(
          <DeleteContactDetails
            contactId={initValues.contactPerson.id as string}
            onAction={handleConfirmationModalAction}
          />,
        )
        break
      default:
    }
  }

  /**
   * Custom validation to check if at least one email or mobile is added
   *
   * @param emails
   * @param mobiles
   * @param actions
   * @returns boolean
   *
   */
  const hasEmailOrMobile = (
    emails: LeadDetails.Email[],
    mobiles: LeadDetails.Mobile[],
    actions: FormikHelpers<typeof initValues>,
  ): boolean => {
    const { setFieldError } = actions

    const hasEmail = emails.some((i) => i.value ?? '' !== '')
    const hasMobile = mobiles.some((i) => i.value ?? '' !== '')

    if (!hasEmail && !hasMobile) {
      setFieldError('contactPerson.email[0].value', REQUIRED_EMAIL)
      setFieldError('contactPerson.mobile[0].value', REQUIRED_PHONE_NUMBER)
      return false
    }

    return true
  }

  const onSubmit = async (values: typeof initValues, actions: FormikHelpers<typeof initValues>) => {
    if (!values.contactPerson.leadId) {
      Toast.toastMessage({ variant: 'error', message: t('missing_msg', { value: 'Lead ID' }) })
      return
    }

    const isEditing = !!data

    const { contactPerson } = values

    if (!hasEmailOrMobile(contactPerson.email, contactPerson.mobile, actions)) {
      return
    }

    const response = await (isEditing ? updateContact(contactPerson) : createContact(contactPerson))

    if (response.status === FETCHING_STATUS.fail) {
      const message = isEditing ? 'lead_contact_update_error' : 'lead_contact_add_error'

      Toast.toastMessage({
        message: response.error?.message ?? message,
        variant: 'error',
      })
      return
    }

    setNewContact?.(contactPerson)
    Toast.toastMessage({
      actionKey: isEditing ? 'update' : 'add',
      subject: 'lead_contact',
      variant: 'success',
    })

    const deletedContactInfoIds = getDeletedContactInfo(initValues.contactPerson, values.contactPerson)

    if (deletedContactInfoIds.length > 0) {
      saveDeletedContactInfo(deletedContactInfoIds)
      return
    }

    fetchContacts()
    onClose(true)
  }

  /**
   * Loop through contact info IDs and make delete request for each
   * Refetch after when any of the requests is successful
   *
   * @param contactInfoIds
   */
  const saveDeletedContactInfo = async (contactInfoIds: string[]) => {
    if (data?.id) {
      const refetch = await contactInfoIds.reduce(async (refetch, curr): Promise<boolean> => {
        const response = await deleteContactInfo(curr, data.id)
        return (await refetch) || response.status === FETCHING_STATUS.success
      }, Promise.resolve(false))

      if (refetch) {
        fetchContacts()
        onClose(true)
      }
    }
  }

  const validationSchema = Yup.lazy((values: any) =>
    Yup.object()
      .shape({
        contactPerson: LeadContactValidationSchema,
      })
      .test('check-lead-duplicate', 'Duplicate contact found.', async function () {
        try {
          const scopes = deduplicateScopes.filter((scope) => changedKeys.includes(scope))
          if (!scopes || scopes.length <= 0) {
            return true
          }
          const isValid = await checkLeadDuplicate(values, scopes) // ✅ async logic here
          return isValid
        } catch (err) {
          return this.createError({ message: 'Error checking duplicates' })
        }
      }),
  )

  const initialContactPerson: LeadDetails.ContactPerson = useMemo(
    () => ({
      id: '',
      contactTypeId: '',
      email: [
        {
          contactInfoTypeId: emailContactInfoTypeId,
          value: '',
          isPrimary: true,
        },
      ],
      fname: '',
      jobTitle: '',
      leadId,
      isActive: true,
      isPrimary: false,
      lname: '',
      mobile: [
        {
          contactInfoTypeId,
          countryISO: currentBranch.countryCode,
          displayValue: '',
          isPrimary: true,
          value: '',
        },
      ],
    }),
    [contactInfoTypeId, currentBranch.countryCode, emailContactInfoTypeId, leadId],
  )

  const initValues = useMemo(
    () => ({
      leadId,
      contactPerson: data
        ? {
            ...data,
            email: data.email.length > 0 ? data.email : initialContactPerson.email,
            mobile: data.mobile.length > 0 ? data.mobile : initialContactPerson.mobile,
          }
        : initialContactPerson,
      // Adding companyInfo values for duplicate check
      companyInfo: isFetchLeadDetailSuccess
        ? leadDetail?.company
        : {
            name: '',
            registrationNumber: '',
          },
    }),
    [initialContactPerson, data, isFetchLeadDetailSuccess, leadDetail?.company, leadId],
  )

  let changedKeys: string[] = []
  const duplicateReasonMap: Record<string, LeadDuplicateReason> = {
    email: 'email_address',
    fname: 'full_name',
    lname: 'full_name',
  }

  const mapDuplicateReason = (key: string): LeadDuplicateReason =>
    duplicateReasonMap[key] ?? (key as LeadDuplicateReason)

  return (
    <>
      <Formik
        initialValues={initValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
        validateOnChange={false}
        validateOnBlur={false}
        enableReinitialize={true}
      >
        {({ dirty, isSubmitting, setFieldValue, submitForm, values }) => {
          changedKeys = []
          Object.keys(values.contactPerson).forEach((key) => {
            const curValue = values.contactPerson[key as keyof LeadDetails.ContactPerson]
            const initValue = initValues.contactPerson[key as keyof LeadDetails.ContactPerson]

            if (isArray(curValue) && isArray(initValue)) {
              if (['email', 'mobile'].includes(key)) {
                const difference = differenceBy(
                  initValue as { value: string }[],
                  (curValue || []) as { value: string }[],
                  'value',
                )
                if (difference.length > 0) {
                  changedKeys.push(mapDuplicateReason(key))
                }
                return
              }
            } else {
              if (curValue !== initValue) {
                changedKeys.push(mapDuplicateReason(key))
              }
            }
          })

          // If the contact is active, we can submit the form
          return (
            <RightPanel
              disableSubmit={!dirty}
              isSubmitting={isBusy || isSubmitting}
              onCancel={() => onClose()}
              onSubmit={initValues.contactPerson.isActive ? submitForm : undefined}
              submitLabel={initValues.contactPerson.isActive ? (data ? 'save_btn' : 'add_new_btn') : undefined}
              title={
                <PanelTitle<keyof typeof Actions>
                  badge={getBadge(initValues.contactPerson.isActive, initValues.contactPerson.isPrimary)}
                  menuOptions={getMenuOptions(
                    isLastActiveContact,
                    initValues.contactPerson.isActive,
                    initValues.contactPerson.isPrimary,
                  )}
                  showMenu={Boolean(data?.id) && !isLastActiveContact}
                  onClickOption={handleContextMenuOptionClick}
                  title="contact_details_lbl"
                />
              }
              width={532}
              zIndex={zIndex}
            >
              <ContactSection disabled={!initValues.contactPerson.isActive} name="contactPerson" />

              {initValues.contactPerson.isActive && !initValues.contactPerson.isPrimary && (
                <Checkbox
                  checked={values.contactPerson.isPrimary}
                  mt={16}
                  name="contactPerson.isPrimary"
                  onChange={() => {
                    setFieldValue('contactPerson.isPrimary', !values.contactPerson.isPrimary)
                  }}
                >
                  mark_as_primary_lbl
                </Checkbox>
              )}

              {initValues.contactPerson.id && !initValues.contactPerson.isActive && (
                <Box display="flex" mt={16}>
                  <Button mr="8px" variant="outline" onClick={() => handleContextMenuOptionClick(Actions.reactivate)}>
                    lead_contact_reactivate
                  </Button>
                  <Button
                    iconColor="red"
                    icon={['fal', 'trash-alt']}
                    onClick={() => handleContextMenuOptionClick(Actions.delete)}
                    variant="criticalOutline"
                  >
                    contact_delete_lbl
                  </Button>
                </Box>
              )}
            </RightPanel>
          )
        }}
      </Formik>
    </>
  )
}

export default ContactDetails
